import { type FormEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthLayout } from "../../components/auth/AuthLayout";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { StatusMessage } from "../../components/ui/StatusMessage";
import { useAuth } from "../../hooks/useAuth";
import { authService } from "../../services/authService";

const SENSITIVE_URL_KEYS = [
  "access_token",
  "refresh_token",
  "token",
  "token_hash",
  "code",
];

function cleanRecoveryUrl() {
  const url = new URL(window.location.href);
  const hashParams = new URLSearchParams(url.hash.replace(/^#/, ""));
  const hasSensitiveQuery = SENSITIVE_URL_KEYS.some((key) =>
    url.searchParams.has(key),
  );
  const hasSensitiveHash = SENSITIVE_URL_KEYS.some((key) =>
    hashParams.has(key),
  );

  if (hasSensitiveQuery || hasSensitiveHash || url.hash) {
    window.history.replaceState(null, document.title, url.pathname);
  }
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const { isConfigured, loading, user, signOut } = useAuth();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [updated, setUpdated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!loading) {
      cleanRecoveryUrl();
    }
  }, [loading]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);
    setUpdated(false);

    if (password.length < 8) {
      setFormError("Password must be at least 8 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setFormError("Passwords do not match.");
      return;
    }

    setIsSubmitting(true);

    try {
      await authService.updatePassword(password);
      setUpdated(true);
      setPassword("");
      setConfirmPassword("");
      await signOut();
      navigate("/login", { replace: true });
    } catch (error) {
      setFormError(
        error instanceof Error
          ? error.message
          : "Unable to update your password.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AuthLayout>
      <div>
        <p className="eyebrow">Account recovery</p>
        <h1 className="mt-3 text-3xl font-semibold tracking-normal text-ink">
          Set a new password
        </h1>
        <p className="mt-3 text-base leading-7 text-graphite/70">
          Choose a new password for your CoachGrid account.
        </p>
      </div>

      <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
        {!isConfigured ? (
          <StatusMessage
            message="Supabase environment variables are missing. Add them before updating your password."
            tone="error"
          />
        ) : null}

        {!loading && !user ? (
          <StatusMessage
            message="This reset link is missing or has expired. Request a new password reset email."
            tone="error"
          />
        ) : null}

        {updated ? (
          <StatusMessage message="Password updated." tone="success" />
        ) : null}

        {formError ? <StatusMessage message={formError} tone="error" /> : null}

        <Input
          autoComplete="new-password"
          label="New password"
          onChange={(event) => setPassword(event.target.value)}
          type="password"
          value={password}
        />
        <Input
          autoComplete="new-password"
          label="Confirm new password"
          onChange={(event) => setConfirmPassword(event.target.value)}
          type="password"
          value={confirmPassword}
        />

        <Button
          disabled={loading || !user}
          fullWidth
          isLoading={isSubmitting}
          type="submit"
        >
          Update password
        </Button>
      </form>

      <p className="mt-6 text-sm text-graphite/70">
        Need a fresh link?{" "}
        <Link className="link" to="/forgot-password">
          Request another reset
        </Link>
      </p>
    </AuthLayout>
  );
}
